import { Injectable, inject, signal } from '@angular/core';
import { HolidayCareGroup, School, GlobalError } from '@awo-care/shared-core';
import { validateHolidayCareGroup } from './holiday-care-group.utils';
import { Firestore, addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { Auth } from 'firebase/auth';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

const COLLECTION = 'holidayCareGroups';

@Injectable({ providedIn: 'root' })
export class HolidayCareGroupService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FIREBASE_AUTH);
  private groupsCollection = collection(this.firestore, COLLECTION);

  private groupsSignal = signal<HolidayCareGroup[]>([]);
  groups = this.groupsSignal.asReadonly();

  async loadAll(): Promise<HolidayCareGroup[]> {
    const snap = await getDocs(this.groupsCollection);
    const groups = snap.docs.map((d) => ({ id: d.id, ...d.data() } as HolidayCareGroup));
    this.groupsSignal.set(groups);
    return groups;
  }

  async getById(groupId: string): Promise<HolidayCareGroup | null> {
    const snap = await getDoc(doc(this.firestore, COLLECTION, groupId));
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as HolidayCareGroup) : null;
  }

  async create(group: Omit<HolidayCareGroup, 'id'>, allSchools: School[]): Promise<string> {
    this.assertValid(group, allSchools, undefined);
    const audit = this.audit();
    const docRef = await addDoc(this.groupsCollection, {
      ...group,
      createdOn: serverTimestamp(),
      createdBy: audit.lastModifiedBy,
      createdByEmail: audit.lastModifiedByEmail,
      ...audit,
    });
    return docRef.id;
  }

  async update(groupId: string, updates: Partial<HolidayCareGroup>, allSchools: School[]): Promise<void> {
    const existing = await this.getById(groupId);
    if (!existing) throw this.error('Verbund nicht gefunden');
    const merged = { ...existing, ...updates };
    this.assertValid(merged, allSchools, groupId);
    await setDoc(doc(this.firestore, COLLECTION, groupId), { ...updates, ...this.audit() }, { merge: true });
  }

  async delete(groupId: string): Promise<void> {
    await deleteDoc(doc(this.firestore, COLLECTION, groupId));
  }

  private assertValid(
    group: Pick<HolidayCareGroup, 'name' | 'kreis' | 'schoolIds'>,
    allSchools: School[],
    selfGroupId: string | undefined,
  ): void {
    const result = validateHolidayCareGroup(group, allSchools, selfGroupId);
    if (result.valid === false) throw this.error(result.reason);
  }

  private audit() {
    const u = this.auth.currentUser;
    return {
      lastModifiedBy: u?.uid ?? 'system',
      lastModifiedByEmail: u?.email ?? 'system@awo-care.org',
      lastModifiedSource: 'employee-ui' as const,
    };
  }

  private error(msg: string): GlobalError {
    const err = new Error(msg) as GlobalError;
    err.header = 'Verbund-Fehler';
    err.message = msg;
    err.bugReport = 'none';
    return err;
  }
}
